import React, { useState, useEffect } from 'react';
import { Container, Snackbar, Alert, Box, Typography, Fade } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import IdeaList from '../components/IdeaList';
import SubmissionCountdown from '../components/SubmissionCountdown';
import FilterControls from '../components/FilterControls';
import DynamicHeroBanner from '../components/DynamicHeroBanner';
import { Idea, SortOption, FilterOptions } from '../types';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useLanguage } from '../contexts/LanguageContext';
import { HomeContent, contentService } from '../services/content';
import SpaceSelector from '../components/SpaceSelector';

const TEST_USER_ID = process.env.REACT_APP_TEST_USER_ID || "00000000-0000-0000-0000-000000000001";

console.log('REACT_APP_NODE_ENV', process.env.REACT_APP_NODE_ENV);
const Home: React.FC = () => {
  const { currentUser } = useAuth();
  const { tenantConfig } = useTenant();
  const { language, t } = useLanguage();
  const queryClient = useQueryClient();
  const [sort, setSort] = useState<SortOption>('mostUpvoted');
  const [filters, setFilters] = useState<FilterOptions>({ tags: [] });
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | undefined>(undefined);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [animateIn, setAnimateIn] = useState(false);
  const [homeContent, setHomeContent] = useState<HomeContent | null>(null);

  // Load tenant-specific content
  useEffect(() => {
    const loadContent = async () => {
      try {
        const content = await contentService.getHomeContent(tenantConfig?.tenant_id, language);
        setHomeContent(content);
      } catch (error) {
        console.error('Failed to load home content:', error);
      }
    };

    loadContent();
  }, [tenantConfig?.tenant_id, language]);

  // Animate in components after initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateIn(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Fetch ideas with React Query
  const {
    data: ideas = [],
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['ideas', sort, filters, tenantConfig?.tenant_type === 'multi_space' ? selectedSpaceId : null, currentUser?.uid],
    queryFn: async () => {
      const uid = currentUser ? currentUser.uid : undefined;
      const userId = uid ? await api.getUserDatabaseUUID(uid) : undefined;
      
      // Include space filtering in the filters object
      // For single-space tenants, don't apply space filtering
      const isMultiSpace = tenantConfig?.tenant_type === 'multi_space';
      const filtersWithSpace = { 
        ...filters,
        // Only include space_id filter for multi-space tenants
        ...(isMultiSpace && selectedSpaceId ? { space_id: selectedSpaceId } : {})
      };
      
      let fetchedIdeasResponse;
      if (!userId) {
        console.error('Failed to convert Firebase UID to database UUID');
        fetchedIdeasResponse = await api.getApprovedIdeas(sort, filtersWithSpace);
      } else {
        fetchedIdeasResponse = await api.getApprovedIdeas(sort, filtersWithSpace, userId);
      }
      
      // Client-side sorting fallback for "mostUpvoted" option
      let sortedIdeas = [...fetchedIdeasResponse];
      if (sort === 'mostUpvoted') {
        sortedIdeas.sort((a, b) => (b.likes || 0) - (a.likes || 0));
      }
      
      return sortedIdeas;
    },
    staleTime: 3 * 60 * 1000, // 3 minutes for ideas
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  const handleSortChange = (newSort: SortOption) => {
    setSort(newSort);
  };

  const handleFilterChange = (newFilters: FilterOptions) => {
    setFilters(newFilters);
  };

  const handleSpaceChange = (spaceId?: string) => {
    setSelectedSpaceId(spaceId);
  };

  // Upvote mutation with optimistic updates
  const upvoteMutation = useMutation({
    mutationFn: (ideaId: string) => api.toggleUpvote(ideaId, false),
    onMutate: async (ideaId) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['ideas'] });

      // Snapshot the previous value
      const previousIdeas = queryClient.getQueryData(['ideas', sort, filters, currentUser?.uid]);

      // Optimistically update to the new value
      queryClient.setQueryData(['ideas', sort, filters, currentUser?.uid], (old: Idea[] | undefined) => {
        if (!old) return old;
        
        return old.map(idea => {
          if (idea.id === ideaId) {
            const newLikes = (idea.likes || 0) + (idea.userUpvoted ? -1 : 1);
            return {
              ...idea,
              likes: newLikes,
              userUpvoted: !idea.userUpvoted,
              liked_by_user: !idea.userUpvoted
            };
          }
          return idea;
        });
      });

      // Return a context object with the snapshotted value
      return { previousIdeas };
    },
    onSuccess: (updatedIdea) => {
      // Show success snackbar
      setSnackbar({
        open: true,
        message: updatedIdea?.liked_by_user || updatedIdea?.userUpvoted
          ? t.home.upvoteAdded
          : t.home.upvoteRemoved,
        severity: 'success'
      });
      
      // Invalidate and refetch ideas to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['ideas'] });
    },
    onError: (err, ideaId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousIdeas) {
        queryClient.setQueryData(['ideas', sort, filters, currentUser?.uid], context.previousIdeas);
      }
      
      console.error('Failed to upvote:', err);
      setSnackbar({
        open: true,
        message: t.home.upvoteFailed,
        severity: 'error'
      });
    },
  });

  const handleUpvote = (ideaId: string) => {
    // Check if user is logged in first
    if (!currentUser) {
      setSnackbar({
        open: true,
        message: t.home.loginToVote,
        severity: 'error'
      });
      return;
    }
    
    upvoteMutation.mutate(ideaId);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <DynamicHeroBanner />
      <Container maxWidth="lg">
        <Box sx={{ 
          py: 4,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            height: '1px',
            background: 'linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,0.1), rgba(0,0,0,0))'
          }
        }}>
          <Fade in={animateIn} timeout={800} style={{ transitionDelay: '100ms' }}>
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  fontWeight: 600, 
                  mb: 2,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: 60,
                    height: 3,
                    backgroundColor: 'secondary.main',
                  }
                }}
              >
                {homeContent?.communityIdeas.title || t.home.fallbackTitle}
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ fontWeight: 400 }}
                dangerouslySetInnerHTML={{ 
                  __html: homeContent?.communityIdeas.subtitle || t.home.fallbackSubtitle
                }}
              />
            </Box>
          </Fade>

          <SubmissionCountdown />

          <Fade in={animateIn} timeout={800} style={{ transitionDelay: '150ms' }}>
            <Box>
              <SpaceSelector
                selectedSpaceId={selectedSpaceId}
                onSpaceChange={handleSpaceChange}
              />
            </Box>
          </Fade>
          
          <Fade in={animateIn} timeout={800} style={{ transitionDelay: '200ms' }}>
            <Box>
              <FilterControls
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                currentSort={sort}
                currentFilters={filters}
              />
            </Box>
          </Fade>

          <Fade in={animateIn} timeout={800} style={{ transitionDelay: '300ms' }}>
            <Box>
              <IdeaList 
                ideas={ideas} 
                loading={loading} 
                error={error ? String(error) : undefined} 
                onUpvote={handleUpvote} 
              />
            </Box>
          </Fade>

          <Snackbar 
            open={snackbar.open} 
            autoHideDuration={4000} 
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            TransitionComponent={Fade}
          >
            <Alert 
              onClose={handleCloseSnackbar} 
              severity={snackbar.severity}
              variant="filled"
              elevation={6}
              sx={{ 
                width: '100%',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Container>
    </>
  );
};

export default Home; 